/**
 * Shared workbook parse + citation helpers for SpreadsheetView and the Ask
 * Puntt spreadsheet citation summary card. Absolute A1 origin must match
 * SpreadsheetView so cited rows/cells cannot drift between the card and grid.
 */
import { type KnowledgeDataRecordProperty } from "../utils/parsers/knowledge-db";

export type SpreadsheetSheet = {
  name: string;
  cells: unknown[][];
  colCount: number;
};

export type ParsedWorkbook = {
  sheets: SpreadsheetSheet[];
};

export type SpreadsheetCitationAnchor = {
  sheetIndex?: number;
  sheetName?: string;
  range?: string;
  row?: number;
};

export const SPREADSHEET_MAX_RENDER_ROWS = 1000;
export const SPREADSHEET_MAX_RENDER_COLS = 50;

export function colLetter(colIndex: number): string {
  let n = colIndex + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

export function parseA1(ref: string): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.toUpperCase());
  if (!m) return null;
  const letters = m[1];
  const row = parseInt(m[2], 10) - 1;
  let col = 0;
  for (const ch of letters) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { col: col - 1, row };
}

export function parseRange(
  range: string,
): { minCol: number; minRow: number; maxCol: number; maxRow: number } | null {
  const parts = range.replace(/\s+/g, "").split(":");
  if (parts.length === 1) {
    const p = parseA1(parts[0]);
    if (!p) return null;
    return { minCol: p.col, minRow: p.row, maxCol: p.col, maxRow: p.row };
  }
  const a = parseA1(parts[0]);
  const b = parseA1(parts[1]);
  if (!a || !b) return null;
  return {
    minCol: Math.min(a.col, b.col),
    minRow: Math.min(a.row, b.row),
    maxCol: Math.max(a.col, b.col),
    maxRow: Math.max(a.row, b.row),
  };
}

/**
 * Parse an xlsx/csv ArrayBuffer into sheets with absolute A1 indexing
 * (openpyxl-compatible). Same contract SpreadsheetView uses for highlights.
 */
export async function parseWorkbookFromArrayBuffer(
  buf: ArrayBuffer,
): Promise<ParsedWorkbook> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(buf, { type: "array", cellFormula: false });
  const sheets: SpreadsheetSheet[] = wb.SheetNames.map((name: string) => {
    const ws = wb.Sheets[name];
    // sheet_to_json({header:1}) indexes rows/cols relative to the sheet's
    // used-range ORIGIN (!ref), but cell anchors and citation ranges are
    // absolute A1 (openpyxl on the backend). Force an absolute A1 origin so
    // a sheet whose data starts at e.g. B2 does not shift every rendered
    // column letter, row number, and anchored cell one step left/up.
    const usedRange = ws?.["!ref"];
    const decodedEnd = usedRange ? XLSX.utils.decode_range(usedRange).e : null;
    // Clamp the PARSE range to the render caps. A stray value/format far down a
    // sheet inflates the declared used-range (!ref) toward Excel's full grid;
    // sheet_to_json over that whole range blocks the main thread even though we
    // only render the first SPREADSHEET_MAX_RENDER_ROWS.
    const aoa = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: true,
      defval: undefined,
      ...(decodedEnd
        ? {
            range: {
              s: { r: 0, c: 0 },
              e: {
                r: Math.min(decodedEnd.r, SPREADSHEET_MAX_RENDER_ROWS - 1),
                c: Math.min(decodedEnd.c, SPREADSHEET_MAX_RENDER_COLS - 1),
              },
            },
          }
        : {}),
    }) as unknown[][];
    const truncated = aoa
      .slice(0, SPREADSHEET_MAX_RENDER_ROWS)
      .map((row) => (row ?? []).slice(0, SPREADSHEET_MAX_RENDER_COLS));
    // Grid width must cover the sheet's DECLARED used range, not just the
    // longest non-empty row: sheet_to_json drops trailing empty cells per
    // row, and a citation/anchor may point at an empty cell past the last
    // populated column.
    const maxRowLen = truncated.reduce(
      (max, row) => Math.max(max, row.length),
      0,
    );
    const refCols = decodedEnd ? decodedEnd.c + 1 : 0;
    const colCount = Math.min(
      Math.max(maxRowLen, refCols),
      SPREADSHEET_MAX_RENDER_COLS,
    );
    return { name, cells: truncated, colCount };
  });
  return { sheets };
}

export function resolveCitedSheetIndex(
  workbook: ParsedWorkbook,
  anchor: SpreadsheetCitationAnchor,
  fallbackSheetIndex = 0,
): number | null {
  if (anchor.sheetIndex != null && workbook.sheets[anchor.sheetIndex] != null) {
    return anchor.sheetIndex;
  }
  const name = anchor.sheetName?.trim().toLowerCase();
  if (name) {
    const idx = workbook.sheets.findIndex(
      (s) => s.name.trim().toLowerCase() === name,
    );
    if (idx >= 0) return idx;
    return null;
  }
  if (anchor.sheetIndex != null) return null;
  if (workbook.sheets[fallbackSheetIndex] != null) return fallbackSheetIndex;
  return workbook.sheets.length > 0 ? 0 : null;
}

/** 0-based row index for the cited row, or null if the anchor has no row. */
export function citedRowIndex0(
  anchor: SpreadsheetCitationAnchor,
): number | null {
  if (anchor.row != null && anchor.row > 0) return anchor.row - 1;
  if (anchor.range) {
    const bounds = parseRange(anchor.range);
    if (bounds) return bounds.minRow;
  }
  return null;
}

/** Instruction / rules blobs are not usable as field labels. */
const MAX_HEADER_LABEL_CHARS = 80;

/** Bare units that show up in nutrition "Serving size" metadata rows. */
const WEAK_HEADER_LABEL = /^(g|mg|µg|ug|mcg|kg|ml|cl|l|kcal|kj|oz|lb|%)$/i;

function cellToDisplayValue(value: unknown): string | number | boolean | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return value;
  return String(value);
}

function isEmptyCellValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

function normalizeHeaderText(value: unknown): string | null {
  if (isEmptyCellValue(value)) return null;
  const text = String(value).replace(/\s+/g, " ").trim();
  if (text.length === 0 || text.length > MAX_HEADER_LABEL_CHARS) return null;
  return text;
}

function isNumericLikeCell(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  return /^-?\d+(\.\d+)?%?$/.test(trimmed.replace(/,/g, ""));
}

/**
 * True column headers are descriptive labels. Reject units ("g"), bare numbers
 * ("100"), and other value-like cells that appear in metadata rows such as
 * Nutrition EDP "Serving size: | 100 | g | g".
 */
function isStrongHeaderLabel(value: unknown): boolean {
  const text = normalizeHeaderText(value);
  if (text == null) return false;
  if (isNumericLikeCell(value) || isNumericLikeCell(text)) return false;
  if (WEAK_HEADER_LABEL.test(text)) return false;
  // Require at least one letter so punctuation-only cells don't qualify.
  return /[A-Za-z\u00C0-\u024F]/.test(text);
}

function shortHeaderLabel(value: unknown): string | null {
  if (!isStrongHeaderLabel(value)) return null;
  return normalizeHeaderText(value);
}

function populatedColumnIndexes(row: unknown[], colCount: number): number[] {
  const cols: number[] = [];
  for (let c = 0; c < colCount; c++) {
    if (!isEmptyCellValue(row[c])) cols.push(c);
  }
  return cols;
}

/**
 * Pick the best header-like row above the cited data among messy real-world
 * workbooks (instructions above the table, metadata rows, repeated category
 * values). Score by how many cited columns have strong descriptive labels —
 * not units, numbers, or instruction blobs.
 *
 * Returns null when no good header row exists — callers fall back to letters.
 */
export function findHeaderRowIndex(
  cells: unknown[][],
  dataRow0: number,
  dataCols: number[],
): number | null {
  if (dataRow0 <= 0 || dataCols.length === 0) return null;

  const minCoverage =
    dataCols.length === 1 ? 1 : Math.max(2, Math.ceil(dataCols.length * 0.5));

  let bestIndex: number | null = null;
  let bestScore = -1;

  for (let r = 0; r < dataRow0; r++) {
    const candidate = cells[r] ?? [];
    let strong = 0;
    let weakOrNumeric = 0;
    let longText = 0;
    for (const c of dataCols) {
      const raw = candidate[c];
      if (isEmptyCellValue(raw)) continue;
      if (normalizeHeaderText(raw) == null) {
        longText += 1;
        continue;
      }
      if (isStrongHeaderLabel(raw)) strong += 1;
      else weakOrNumeric += 1;
    }
    // Instruction blobs disqualify the row (e.g. Nutrition rules in row 1).
    if (longText > 0) continue;
    if (strong < minCoverage) continue;
    // A real header row is mostly labels, not values/units.
    if (strong < weakOrNumeric) continue;

    // Prefer denser strong coverage; lightly penalize leftover value cells.
    // Earlier rows win exact ties (table header before a later section title).
    const score = strong * 10 - weakOrNumeric * 3;
    if (score > bestScore) {
      bestScore = score;
      bestIndex = r;
    }
  }
  return bestIndex;
}

/** Stable fake Mongo ids so Preview's KnowledgeDataRecordProperty type is satisfied. */
function syntheticPropertyId(colIndex: number): string {
  return `00000000000000000000${String(colIndex).padStart(4, "0")}`;
}

/**
 * Map a cited spreadsheet row to Preview key-value fields.
 * - Data rows: best header-like row above the citation for labels;
 *   empty/missing header → column letter. Prefers dense column headers over
 *   intervening data rows or sparse metadata rows.
 * - When the citation is itself row 1, or no usable header row is found:
 *   column letters as labels (keeps A1 fixtures producing fields).
 * Empty values are skipped.
 */
export function citedRowToKnowledgeFields(
  workbook: ParsedWorkbook,
  anchor: SpreadsheetCitationAnchor,
): {
  fields: KnowledgeDataRecordProperty[];
  sheetName: string;
  rowNumber: number;
} | null {
  const sheetIdx = resolveCitedSheetIndex(workbook, anchor);
  if (sheetIdx == null) return null;
  const sheet = workbook.sheets[sheetIdx];
  if (!sheet) return null;

  const row0 = citedRowIndex0(anchor);
  if (row0 == null) return null;
  if (row0 < 0 || row0 >= sheet.cells.length) return null;

  const row = sheet.cells[row0] ?? [];
  const dataCols = populatedColumnIndexes(row, sheet.colCount);
  const headerRowIndex =
    row0 === 0 ? null : findHeaderRowIndex(sheet.cells, row0, dataCols);
  const header =
    headerRowIndex != null ? (sheet.cells[headerRowIndex] ?? []) : [];
  const useColumnLetters = headerRowIndex == null;
  const fields: KnowledgeDataRecordProperty[] = [];

  for (let c = 0; c < sheet.colCount; c++) {
    const raw = row[c];
    if (isEmptyCellValue(raw)) continue;
    const value = cellToDisplayValue(raw);
    if (value === null) continue;

    let name: string;
    if (useColumnLetters) {
      name = colLetter(c);
    } else {
      name = shortHeaderLabel(header[c]) ?? colLetter(c);
    }

    const id = syntheticPropertyId(c);
    fields.push({
      _id: id,
      propertyId: id,
      name,
      value,
    });
  }

  return {
    fields,
    sheetName: sheet.name,
    rowNumber: row0 + 1,
  };
}

export function filenameFromSpreadsheetUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    const base = path.split("/").filter(Boolean).pop() ?? "";
    return decodeURIComponent(base) || "Spreadsheet";
  } catch {
    const base = url.split("?")[0]?.split("/").filter(Boolean).pop() ?? "";
    try {
      return decodeURIComponent(base) || "Spreadsheet";
    } catch {
      return base || "Spreadsheet";
    }
  }
}
